
// updated after show the company details in top when ad new company
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { showToast } from "../../utils/customToast";
import { SECURITY_KEY } from "../../globals";
import "../Master/Ledger/UpdateLedger.css";
import { useCompany } from "../../context/CompanyContext"; // ⬅ added
import { apiWithLog } from "../../utils/apiWithLog";
import { fetchUserCompanyLimit } from "../../utils/companyLimitHelper";

const generateCompanyCode = (name) => {
  if (!name) return "";
  const words = name
    .trim()
    .split(/[\s\-_&,./\\|()]+/)
    .filter(Boolean);
  return words.map((w) => w[0].toUpperCase()).join("");
};

function Compney() {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const { setSelectedCompany } = useCompany(); // ⬅ added for navbar update

  const [formData, setFormData] = useState({
    Name: "",
    CompanyCode: "",
    CompanyColourCode: "#000000",
    Address: "",
    TelNo: "",
    GstNo: "",
    StateID: "",
    BankName: "",
    BankAccountNo: "",
    IFSCCode: "",
    PanNo: "",
  });

  const [isCodeManual, setIsCodeManual] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [stateList, setStateList] = useState([]);
  const [stateCodeMap, setStateCodeMap] = useState({});
  const [stateSearch, setStateSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [limitInfo, setLimitInfo] = useState({
    maxCompanies: null,
    currentCount: 0,
    canAddCompany: true,
    isLimitReached: false,
    isSuperAdmin: false,
  });
  const [checkingLimit, setCheckingLimit] = useState(true);

  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  // Check company limit based on MasterData.MaxCompanies
  useEffect(() => {
    const checkLimit = async () => {
      try {
        setCheckingLimit(true);
        const info = await fetchUserCompanyLimit();
        setLimitInfo(info);
      } catch (err) {
        console.warn("Could not check company limit:", err);
      } finally {
        setCheckingLimit(false);
      }
    };

    checkLimit();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "LogoPath") {
      if (files && files.length > 0) {
        const file = files[0];
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      }
    } else if (name === "Name") {
      setFormData((prev) => {
        const updated = { ...prev, Name: value };
        if (!isCodeManual) {
          updated.CompanyCode = generateCompanyCode(value);
        }
        return updated;
      });
    } else if (name === "CompanyCode") {
      setIsCodeManual(true);
      setFormData((prev) => ({ ...prev, CompanyCode: value.toUpperCase() }));
    } else if (name === "CompanyColourCode") {
      setFormData((prev) => ({ ...prev, CompanyColourCode: value }));
    } else if (name === "GstNo" || name === "PanNo" || name === "IFSCCode") {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      const apiUrl = `${BASE_REST_API_URL}/SelectData.aspx`;

      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "GSTStates");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: data.toString(),
        });

        const result = await response.json();
        if (Array.isArray(result.Response)) {
          const sortedStates = result.Response.sort((a, b) =>
            a.State.localeCompare(b.State)
          );
          setStateList(sortedStates);

          const map = {};
          sortedStates.forEach((state) => {
            map[state.ID] = state.ID.toString().padStart(2, "0");
          });
          setStateCodeMap(map);
        } else {
          showToast.error("No states found in API response.");
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        showToast.error("Error fetching state list.");
      }
    };

    fetchStates();
  }, []);

  const isGSTValid = () => {
    if (!formData.GstNo || !formData.StateID) return true;
    const firstTwoGST = formData.GstNo.substring(0, 2);
    const selectedStateId = stateCodeMap[formData.StateID];
    return firstTwoGST === selectedStateId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const loggedInUserId = localStorage.getItem("userId");
    if (!loggedInUserId) {
      showToast.error("User not logged in!");
      return;
    }

    // ===== Company Limit Validation =====
    if (limitInfo.isLimitReached) {
      showToast.error(
        `Cannot add company! You have reached your maximum limit of ${limitInfo.maxCompanies} companies allowed according to your account configuration.`
      );
      return;
    }

    // ===== Validation =====
    if (formData.GstNo.length !== 15) {
      showToast.error("GST No must be exactly 15 characters!");
      return;
    }

    if (!isGSTValid()) {
      showToast.error(
        "GST No first two digits do not match the selected state's ID!"
      );
      return;
    }

    // ===== Duplicate Telephone / Mobile Validation =====
    const cleanTel = String(formData.TelNo || "").replace(/[\s\-+()]/g, "").slice(-10);
    if (cleanTel) {
      try {
        const checkData = new URLSearchParams();
        checkData.append("SecurityKey", SECURITY_KEY);
        checkData.append("TableName", "Company");
        checkData.append("WhereCondition", `MasterId=${loggedInUserId}`);
        checkData.append("*", "*");

        const checkRes = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: checkData.toString(),
        });

        if (checkRes.ok) {
          const checkJson = await checkRes.json();
          if (Array.isArray(checkJson?.Response)) {
            const exists = checkJson.Response.some((c) => {
              const cTel = String(c.TelNo || "").replace(/[\s\-+()]/g, "").slice(-10);
              return cTel && cTel === cleanTel;
            });
            if (exists) {
              showToast.error("A company with this mobile/telephone number already exists! Duplicate numbers are not allowed.");
              return;
            }
          }
        }
      } catch (err) {
        console.warn("Could not check duplicate company telephone:", err);
      }
    }

    const apiUrl = `${BASE_REST_API_URL}/insertdata.aspx`;

    // ===== FormData (File Upload Supported) =====
    const finalCode = formData.CompanyCode || generateCompanyCode(formData.Name);
    const finalColor = formData.CompanyColourCode || "#000000";

    const data = new FormData();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "Company");
    data.append("MasterId", loggedInUserId);
    data.append("Name", formData.Name);
    data.append("CompanyCode", finalCode);
    data.append("CompanyColourCode", finalColor);
    data.append("Address", formData.Address);
    data.append("TelNo", formData.TelNo);
    data.append("GstNo", formData.GstNo);
    data.append("StateID", formData.StateID);
    data.append("BankName", formData.BankName);
    data.append("BankAccountNo", formData.BankAccountNo);
    data.append("IFSCCode", formData.IFSCCode);
    data.append("PanNo", formData.PanNo || "");

    if (logoFile) {
      data.append("LogoPath", logoFile);
    }

    try {
      setLoading(true);

      // ===== API Call with Logging =====
      const res = await apiWithLog({
        method: "POST",
        url: apiUrl,
        data: data, // FormData directly
        module: "Company",
        action: "A",
      });

      // ASP.NET may return text or JSON
      let result = res?.data || res;

      if (typeof result === "string") {
        try {
          result = JSON.parse(result);
        } catch {
          result = { Status: "Success", InsertedId: null };
        }
      }

      console.log("Company Add Response:", result);

      showToast.save("Company added successfully!");

      // ===== Update Navbar Immediately =====
      setSelectedCompany({
        id: result?.InsertedId || null,
        name: formData.Name,
      });

      // ===== Redirect to Company Selection =====
      setTimeout(() => {
        window.location.href = "/selectcompny";
      }, 800);

    } catch (error) {
      console.error("Company Add Error:", error);
      showToast.error("Something went wrong while adding the company.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update_ledger_container">
      {/* Header & Breadcrumbs */}
      <div className="update_ledger_header_bar">
        <div className="update_ledger_title_group">
          <h4 className="update_ledger_title">Add Company</h4>
          <div className="update_ledger_breadcrumbs">
            <Link to="/home">Home</Link>
            <span>/</span>
            <Link to="/selectcompny">Company</Link>
            <span>/</span>
            <span>New</span>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="update_ledger_card">
        <div className="update_ledger_card_header">
          <h5>Company Information</h5>
        </div>

        {limitInfo.isLimitReached && (
          <div
            style={{
              margin: "16px 24px 0 24px",
              padding: "16px 20px",
              backgroundColor: "#fef2f2",
              border: "1px solid #f87171",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
              <div>
                <strong style={{ color: "#991b1b", fontSize: "15px", display: "block" }}>
                  Company Limit Reached ({limitInfo.currentCount} / {limitInfo.maxCompanies})
                </strong>
                <span style={{ color: "#7f1d1d", fontSize: "13px" }}>
                  Your account allows a maximum of {limitInfo.maxCompanies} companies based on MasterData configuration. You cannot create another company.
                </span>
              </div>
            </div>
            <Link
              to="/selectcompny"
              style={{
                backgroundColor: "#dc2626",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: "600",
                fontSize: "13px",
              }}
            >
              Back to Companies
            </Link>
          </div>
        )}

        <form
          ref={formRef}
          encType="multipart/form-data"
          onSubmit={handleSubmit}
        >
          <div className="update_ledger_grid">
            <div className="update_ledger_field">
              <label htmlFor="Name">
                Company Name <span className="required">*</span>
              </label>
              <input
                id="Name"
                type="text"
                name="Name"
                value={formData.Name}
                onChange={handleChange}
                placeholder="e.g. Acme Tours & Travels"
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="CompanyCode">
                Company Code
              </label>
              <input
                id="CompanyCode"
                type="text"
                name="CompanyCode"
                value={formData.CompanyCode}
                onChange={handleChange}
                placeholder="e.g. DTT"
                style={{ textTransform: "uppercase", fontWeight: "600", letterSpacing: "1px" }}
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="CompanyColourCode">
                Company Color Code (Default: Black)
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  id="CompanyColourCodePicker"
                  type="color"
                  name="CompanyColourCode"
                  value={formData.CompanyColourCode || "#000000"}
                  onChange={handleChange}
                  style={{
                    width: "44px",
                    height: "38px",
                    padding: "2px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                  title="Pick Color"
                />
                <input
                  id="CompanyColourCode"
                  type="text"
                  name="CompanyColourCode"
                  value={formData.CompanyColourCode}
                  onChange={handleChange}
                  placeholder="#000000"
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div className="update_ledger_field">
              <label htmlFor="Address">
                Address <span className="required">*</span>
              </label>
              <input
                id="Address"
                type="text"
                name="Address"
                value={formData.Address}
                onChange={handleChange}
                placeholder="Full Address"
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="TelNo">
                Telephone Number <span className="required">*</span>
              </label>
              <input
                id="TelNo"
                type="text"
                name="TelNo"
                value={formData.TelNo}
                onChange={handleChange}
                placeholder="e.g. +91 9876543210"
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="GstNo">
                GST No (15 digits) <span className="required">*</span>
              </label>
              <input
                id="GstNo"
                type="text"
                name="GstNo"
                value={formData.GstNo}
                onChange={handleChange}
                placeholder="e.g. 08ABCDE1234F1Z5"
                style={{ textTransform: "uppercase" }}
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="StateID">
                State <span className="required">*</span>
              </label>
              <select id="StateID" name="StateID" value={formData.StateID} onChange={handleChange} required>
                <option value="">-- Select State --</option>
                {stateList.map((state) => (
                  <option key={state.ID} value={state.ID}>
                    {state.State} ({state.ID})
                  </option>
                ))}
              </select>
            </div>

            <div className="update_ledger_field">
              <label htmlFor="PanNo">PAN No</label>
              <input
                id="PanNo"
                type="text"
                name="PanNo"
                value={formData.PanNo}
                onChange={handleChange}
                placeholder="e.g. ABCDE1234F"
                style={{ textTransform: "uppercase" }}
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="BankName">
                Bank Name <span className="required">*</span>
              </label>
              <input
                id="BankName"
                type="text"
                name="BankName"
                value={formData.BankName}
                onChange={handleChange}
                placeholder="e.g. State Bank of India"
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="BankAccountNo">
                Bank Account No <span className="required">*</span>
              </label>
              <input
                id="BankAccountNo"
                type="text"
                name="BankAccountNo"
                value={formData.BankAccountNo}
                onChange={handleChange}
                placeholder="Account Number"
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="IFSCCode">
                IFSC Code <span className="required">*</span>
              </label>
              <input
                id="IFSCCode"
                type="text"
                name="IFSCCode"
                value={formData.IFSCCode}
                onChange={handleChange}
                placeholder="e.g. SBIN0001234"
                style={{ textTransform: "uppercase" }}
                required
              />
            </div>

            <div className="update_ledger_field">
              <label htmlFor="LogoPath">Company Logo</label>
              <input
                ref={fileInputRef}
                id="LogoPath"
                type="file"
                name="LogoPath"
                accept="image/*"
                onChange={handleChange}
              />
              {logoPreview && (
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <img
                    src={logoPreview}
                    alt="Company Logo Preview"
                    style={{
                      width: "70px",
                      height: "70px",
                      objectFit: "contain",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "4px",
                      background: "#fff",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#dc2626",
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    title="Remove Selected Logo"
                  >
                    <i className="fa-solid fa-trash-can"></i> Remove Logo
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="update_ledger_actions">
            <Link to="/selectcompny" className="update_ledger_cancel_btn">
              Cancel
            </Link>
            <button
              type="submit"
              className="update_ledger_submit_btn"
              disabled={loading || limitInfo.isLimitReached}
              style={
                limitInfo.isLimitReached
                  ? {
                      backgroundColor: "#a0aec0",
                      cursor: "not-allowed",
                      boxShadow: "none",
                    }
                  : {}
              }
              title={
                limitInfo.isLimitReached
                  ? `Limit reached: ${limitInfo.currentCount}/${limitInfo.maxCompanies} companies`
                  : ""
              }
            >
              {loading
                ? "Saving..."
                : limitInfo.isLimitReached
                ? "Company Limit Reached"
                : "Save Company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Compney;
